/**
 * ProjectTimeContext - Provides active project context for time tracking
 * Project Hub sets the active project when the user is working on a project
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from '../utils/AuthContext';
import { useProjectTimeTracking } from '../hooks/useProjectTimeTracking';
import { isCrmStaffUser } from '../utils/userRoles';
import type { ProjectPhase } from '../types';

interface ProjectTimeContextValue {
  activeProjectId: string | null;
  activeProjectPhase: ProjectPhase | null;
  elapsedSeconds: number;
  setActiveProject: (projectId: string | null, phase?: ProjectPhase | null) => void;
}

const ProjectTimeContext = createContext<ProjectTimeContextValue | undefined>(undefined);

export const useProjectTimeContext = () => {
  const ctx = useContext(ProjectTimeContext);
  if (ctx === undefined) {
    throw new Error('useProjectTimeContext must be used within ProjectTimeProvider');
  }
  return ctx;
};

export const ProjectTimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [activeProjectPhase, setActiveProjectPhase] = useState<ProjectPhase | null>(null);

  const setActiveProject = useCallback(
    (projectId: string | null, phase?: ProjectPhase | null) => {
      setActiveProjectId(projectId);
      setActiveProjectPhase(phase ?? null);
    },
    [],
  );

  const trackTime = isCrmStaffUser(user?.role) && user?.is1099Contractor !== true;
  const { elapsedSeconds } = useProjectTimeTracking({
    projectId: activeProjectId,
    phase: activeProjectPhase,
    enabled: trackTime && !!activeProjectId,
  });

  const value: ProjectTimeContextValue = {
    activeProjectId,
    activeProjectPhase,
    elapsedSeconds,
    setActiveProject,
  };

  return (
    <ProjectTimeContext.Provider value={value}>
      {children}
    </ProjectTimeContext.Provider>
  );
};
